/**
 * Screenshot capture for a bundle.
 *
 * A browser has no equivalent of Electron's `capturePage()`, so there is no way
 * to composite the real window. v1 reuses the 2D canvas snapshot the editor
 * already maintains in the store; capturing the surrounding DOM chrome — the
 * panels, dialogs and error toast that usually carry the diagnosis — is pending
 * a timeboxed spike, which is also where the react-three-fiber viewport gets
 * settled (reading a WebGL buffer at all needs `preserveDrawingBuffer`, which
 * costs frame-rate permanently to serve a rare feature).
 *
 * Everything here returns `undefined` rather than throwing or substituting a
 * placeholder. A zero-byte part would collapse "capture failed" into "the user
 * opted out", and those two states are exactly what the flag/part pair exists
 * to keep apart.
 */

const DATA_URL_PREFIX = 'data:'
const BASE64_MARKER = ';base64,'

/** Decodes a `data:` URL's base64 payload, or `undefined` if there isn't one. */
export function dataUrlToBytes(dataUrl: string | null | undefined): Uint8Array | undefined {
  if (!dataUrl || !dataUrl.startsWith(DATA_URL_PREFIX)) return undefined

  const markerAt = dataUrl.indexOf(BASE64_MARKER)
  if (markerAt === -1) return undefined

  const payload = dataUrl.slice(markerAt + BASE64_MARKER.length)
  if (payload === '') return undefined

  try {
    const binary = atob(payload)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  } catch {
    // Capture runs on a keystroke against a page in any state; a decode failure
    // must cost the screenshot, never the bundle.
    return undefined
  }
}

/**
 * Reads the current screenshot without consuming it.
 *
 * Peeked rather than drained on purpose: a submission can fail transiently and
 * the dialog's designed recovery is to stay open so the user can press Send
 * again. If reading consumed the capture, that retry would ship
 * `screenshotOptedOut: "false"` with no screenshot part — which the wire
 * defines as "capture failed", sending triage after a client bug that never
 * happened while the user sees an unqualified success.
 */
export function peekScreenshot(getSnapshot: () => string | null): Uint8Array | undefined {
  try {
    return dataUrlToBytes(getSnapshot())
  } catch {
    return undefined
  }
}
